import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout, { Section } from "@/components/layout/Layout";
import JsonLd from "@/components/common/JsonLd";
import DetailNotFound from "@/components/common/DetailNotFound";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Paperclip } from "lucide-react";

export default function RoleDetail() {
    const { id } = useParams();
    const [role, setRole] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", portfolio: "", note: "" });
    const [resume, setResume] = useState(null);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    // Tracks whether the render that started an in-flight application
    // submission is still the active one — i.e. the role id hasn't
    // changed and the component hasn't unmounted since. submit() captures
    // this token before its POST and checks it after, so a Role A
    // submission that completes after the user has already navigated to
    // Role B (or away entirely) can't touch state that no longer belongs
    // to it. This doesn't cancel or undo the request itself — the POST
    // already reached the backend regardless; it only gates whether the
    // UI reacts to its result.
    const activeRef = useRef({ id, alive: true });

    useEffect(() => {
        // Reset on every id change — otherwise a previous role (or a
        // previous "not found"/submitted-application state, including a
        // stuck "sending…") stays on screen while the new request is in
        // flight. `cancelled` guards against a stale GET response landing
        // after a newer one already resolved, or after unmount.
        const token = { id, alive: true };
        activeRef.current = token;
        let cancelled = false;
        setRole(null);
        setForm({ name: "", email: "", portfolio: "", note: "" });
        setResume(null);
        setSending(false);
        setDone(false);
        api.get(`/roles/${id}`)
            .then((r) => { if (!cancelled) setRole(r.data); })
            .catch(() => { if (!cancelled) setRole(false); });
        return () => { cancelled = true; token.alive = false; };
    }, [id]);

    const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) { toast.error("Please share your name and email."); return; }
        const token = activeRef.current;
        setSending(true);
        try {
            const fd = new FormData();
            fd.append("name", form.name); fd.append("email", form.email); fd.append("role", role?.title || id);
            if (form.portfolio) fd.append("portfolio", form.portfolio);
            if (form.note) fd.append("note", form.note);
            if (resume) fd.append("resume", resume);
            await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
            // The request already reached the backend either way — this
            // only decides whether ITS RESULT still gets to affect the UI.
            if (!token.alive) return;
            setDone(true);
            toast.success("Application received. We will read it carefully.");
        } catch {
            if (!token.alive) return;
            toast.error("Something went wrong. Please try again.");
        } finally {
            if (token.alive) setSending(false);
        }
    };

    if (role === false) {
        return (
            <DetailNotFound title="Not found — Ventara" heading="This role is not here."
                            backTo="/careers" backLabel="Back to Careers" />
        );
    }
    if (!role) {
        return (
            <Layout title="Loading — Ventara" description="Loading this role.">
                <Section className="pt-40"><p>Loading…</p></Section>
            </Layout>
        );
    }

    return (
        <Layout title={`${role.title} — Careers · Ventara`} description={role.brief}>
            <JsonLd id="jobposting" data={{
                "@context": "https://schema.org",
                "@type": "JobPosting",
                "title": role.title,
                "description": [role.brief, ...(role.responsibilities || []), ...(role.requirements || [])].filter(Boolean).join(" \u2014 "),
                "datePosted": new Date().toISOString().slice(0, 10),
                "employmentType": "FULL_TIME",
                "hiringOrganization": {
                    "@type": "Organization",
                    "name": "Ventara",
                    "sameAs": "https://iventara.com/",
                    "logo": "https://iventara.com/logo.png"
                },
                "jobLocationType": (role.loc || "").toLowerCase().includes("remote") ? "TELECOMMUTE" : undefined,
                "applicantLocationRequirements": { "@type": "Country", "name": "Worldwide" },
                "jobLocation": {
                    "@type": "Place",
                    "address": { "@type": "PostalAddress", "addressLocality": role.loc || "Remote" }
                },
                "url": `https://iventara.com/careers/${role.id || id}`
            }} />
            <JsonLd id="breadcrumb-role" data={{
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://iventara.com/" },
                    { "@type": "ListItem", "position": 2, "name": "Careers", "item": "https://iventara.com/careers" },
                    { "@type": "ListItem", "position": 3, "name": role.title, "item": `https://iventara.com/careers/${role.id || id}` }
                ]
            }} />
            <Section className="pt-32">
                <Link to="/careers" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4" /> Careers
                </Link>
                <p className="label mt-10">{role.team} · {role.loc}</p>
                <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl mt-6 tracking-tight leading-[0.98] max-w-4xl">{role.title}</h1>
                <p className="prose-body text-lg mt-8 max-w-2xl">{role.brief}</p>
            </Section>

            <Section className="pt-16 pb-24 grid lg:grid-cols-12 gap-16">
                <div className="lg:col-span-5 space-y-10">
                    <div>
                        <p className="label">Responsibilities</p>
                        <ul className="mt-6 space-y-4">
                            {role.responsibilities?.map((r, i) => (
                                <li key={i} className="prose-body flex gap-3"><span className="text-foreground/50">0{i + 1}</span>{r}</li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <p className="label">What we look for</p>
                        <ul className="mt-6 space-y-4">
                            {role.requirements?.map((r, i) => (
                                <li key={i} className="prose-body flex gap-3"><span className="text-foreground/50">·</span>{r}</li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    {done ? (
                        <div className="border border-border rounded-3xl p-12" data-testid="apply-success">
                            <p className="label">Received</p>
                            <h2 className="font-serif font-light text-4xl mt-4 tracking-tight">Thank you.</h2>
                            <p className="prose-body mt-4">Someone from the studio will be in touch within two working days.</p>
                        </div>
                    ) : (
                    <form onSubmit={submit} data-testid="apply-form" className="space-y-8">
                        <p className="label">Apply</p>
                        <h2 className="font-serif font-light text-3xl sm:text-4xl tracking-tight">Tell us about you.</h2>
                        <div className="grid sm:grid-cols-2 gap-6">
                            <div>
                                <Label className="label">Your name</Label>
                                <Input data-testid="apply-name" value={form.name} onChange={on("name")} className="mt-3 h-12 bg-transparent border-0 border-b border-border rounded-none px-0 text-lg font-serif focus-visible:ring-0 focus-visible:border-foreground" />
                            </div>
                            <div>
                                <Label className="label">Email</Label>
                                <Input type="email" data-testid="apply-email" value={form.email} onChange={on("email")} className="mt-3 h-12 bg-transparent border-0 border-b border-border rounded-none px-0 text-lg font-serif focus-visible:ring-0 focus-visible:border-foreground" />
                            </div>
                        </div>
                        <div>
                            <Label className="label">Portfolio or LinkedIn</Label>
                            <Input data-testid="apply-portfolio" value={form.portfolio} onChange={on("portfolio")} className="mt-3 h-12 bg-transparent border-0 border-b border-border rounded-none px-0 text-lg font-serif focus-visible:ring-0 focus-visible:border-foreground" />
                        </div>
                        <div>
                            <Label className="label">A few words</Label>
                            <Textarea rows={5} data-testid="apply-note" value={form.note} onChange={on("note")} className="mt-3 bg-transparent border-0 border-b border-border rounded-none px-0 text-lg font-serif resize-none focus-visible:ring-0 focus-visible:border-foreground" />
                        </div>
                        <div>
                            <Label className="label">Resume (PDF or DOCX, up to 8MB)</Label>
                            <div className="mt-4">
                                <label className="inline-flex items-center gap-3 rounded-full border border-border px-5 py-3 text-sm cursor-pointer hover:border-foreground/40 transition-colors">
                                    <Paperclip className="h-4 w-4" />
                                    <span>{resume ? resume.name : "Attach resume"}</span>
                                    <input type="file" accept=".pdf,.doc,.docx,.txt" data-testid="apply-resume"
                                           onChange={(e) => setResume(e.target.files?.[0] || null)} className="hidden" />
                                </label>
                            </div>
                        </div>
                        <Button type="submit" data-testid="apply-submit" disabled={sending}
                                className="rounded-full h-12 px-8 bg-foreground text-background hover:bg-foreground/90 text-sm">
                            {sending ? "Sending…" : "Submit application"}
                        </Button>
                    </form>)}
                </div>
            </Section>
        </Layout>
    );
}
